import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface ResourceRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  colorBg: string | null;
  colorText: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use ResourceRole instead */
export type AdminRole = ResourceRole;

interface ResourceRolesResponse {
  roles: ResourceRole[];
}

interface ResourceRoleResponse {
  role: ResourceRole;
}

interface CreateRoleInput {
  code: string;
  name: string;
  description?: string;
  colorBg?: string;
  colorText?: string;
  sortOrder?: number;
}

interface UpdateRoleInput {
  code?: string;
  name?: string;
  description?: string | null;
  colorBg?: string | null;
  colorText?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

// ============ Query Hooks ============

/**
 * Fetch all resource roles
 */
export function useResourceRoles(includeInactive = false) {
  return useQuery({
    queryKey: ['admin-roles', { includeInactive }],
    queryFn: () =>
      api.get<ResourceRolesResponse>('/admin/roles', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - roles don't change often
  });
}

/** @deprecated Use useResourceRoles instead */
export const useAdminRoles = useResourceRoles;

/**
 * Fetch a single resource role by ID
 */
export function useResourceRole(id: string | null) {
  return useQuery({
    queryKey: ['admin-roles', id],
    queryFn: () => api.get<ResourceRoleResponse>(`/admin/roles/${id}`),
    enabled: !!id,
  });
}

/** @deprecated Use useResourceRole instead */
export const useAdminRole = useResourceRole;

// ============ Mutation Hooks ============

/**
 * Create a new resource role
 */
export function useCreateResourceRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRoleInput) =>
      api.post<ResourceRoleResponse>('/admin/roles', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
  });
}

/** @deprecated Use useCreateResourceRole instead */
export const useCreateAdminRole = useCreateResourceRole;

/**
 * Update an existing resource role
 */
export function useUpdateResourceRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateRoleInput) =>
      api.patch<ResourceRoleResponse>(`/admin/roles/${id}`, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-roles', variables.id] });
    },
  });
}

/** @deprecated Use useUpdateResourceRole instead */
export const useUpdateAdminRole = useUpdateResourceRole;

/**
 * Delete (soft-delete) a resource role
 */
export function useDeleteResourceRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
  });
}

/** @deprecated Use useDeleteResourceRole instead */
export const useDeleteAdminRole = useDeleteResourceRole;
